import { heroImage } from '../data/copy'

const TITLE =
  'The Coach’s Operating System™ — Complete Track & Field Training & Management System'
const DESCRIPTION =
  '150+ ready-to-run sessions, 6 complete volumes and 5 command kits for track & field coaches. Build your championship season in minutes, not hours.'

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  )
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.content = content
}

function appendLink(attrs: Record<string, string>): HTMLLinkElement {
  const link = document.createElement('link')
  Object.entries(attrs).forEach(([key, value]) => link.setAttribute(key, value))
  document.head.appendChild(link)
  return link
}

/**
 * Prepares the <head> before the first render:
 * - language, title and social metadata (SEO / sharing);
 * - preload of the hero image, which is the LCP element.
 */
export function setupHead(): void {
  if (typeof document === 'undefined') return

  document.documentElement.lang = 'en'
  document.title = TITLE

  upsertMeta('name', 'description', DESCRIPTION)
  upsertMeta('name', 'theme-color', '#001A4D')
  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:title', TITLE)
  upsertMeta('property', 'og:description', DESCRIPTION)
  upsertMeta('property', 'og:image', heroImage)
  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', TITLE)
  upsertMeta('name', 'twitter:description', DESCRIPTION)
  upsertMeta('name', 'twitter:image', heroImage)

  if (document.head.querySelector('link[data-mp-head]')) return

  appendLink({
    rel: 'preconnect',
    // No crossorigin: images are no-cors requests, so this connection is reused.
    href: 'https://cdn.magicpatterns.com',
    'data-mp-head': 'cdn',
  })

  appendLink({
    rel: 'preload',
    as: 'image',
    href: heroImage,
    fetchpriority: 'high',
    'data-mp-head': 'hero',
  })

  // Fonts: preconnect + non-blocking stylesheet (media swap trick). Text paints
  // immediately in the fallback font and swaps when the web font arrives.
  appendLink({ rel: 'preconnect', href: 'https://fonts.googleapis.com' })
  appendLink({
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossorigin: '',
  })
  const fonts = appendLink({
    rel: 'stylesheet',
    href: FONTS_URL,
    media: 'print',
    'data-mp-head': 'fonts',
  })
  fonts.onload = () => {
    fonts.media = 'all'
  }
}

/** Only the weights the page actually uses. */
const FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&family=Playfair+Display:wght@700&display=swap'
